/**
 * Basisbausteine nach docs/DESIGN.md.
 *
 * Bewusst klein gehalten: flache Flächen, feine Linien, keine Schatten.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export function PageHeader({
  label,
  title,
  nav,
  actions,
  children,
}: {
  label?: string;
  title: string;
  /** Neben dem Titel, etwa zum Blättern zwischen Tagen. */
  nav?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="mb-8 border-b border-line pb-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {label ? <div className="label mb-2">· {label}</div> : null}
          {nav ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <h1 className="text-3xl">{title}</h1>
              {nav}
            </div>
          ) : (
            <h1 className="text-3xl">{title}</h1>
          )}
          {children ? <div className="mt-2 text-sm text-muted">{children}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}

export function Section({
  label,
  title,
  actions,
  children,
}: {
  label?: string;
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mb-10">
      {title ? (
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            {label ? <div className="label mb-1">{label}</div> : null}
            <h2 className="text-lg">{title}</h2>
          </div>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** Zahl in Mono, rechtsbündig, negative Beträge in Alarmfarbe. */
export function Amount({
  value,
  negative,
  muted,
  className = "",
}: {
  value: string;
  negative?: boolean;
  muted?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`num ${negative ? "text-alert" : muted ? "text-muted" : ""} ${className}`}
    >
      {value}
    </span>
  );
}

/** Flags, die die Freigabe blockieren (Spec 2.9.4). */
const BLOCKING = new Set([
  "MISSING_REPORT",
  "MISSING_CLOSING",
  "NEGATIVE_TIP",
  "SPLIT_REPORT",
  "SPLIT_CLOSING",
  "UNKNOWN_WORKSTATION",
  "HIGH_TIP",
  "NEGATIVE_REVENUE",
  "CARRYOVER_FROM_COUNTERPART",
]);

/** Kurzform für das Chip, Langform als Tooltip mit der Begründung. */
const FLAG_LABELS: Record<string, { kurz: string; lang: string }> = {
  MISSING_REPORT: {
    kurz: "Kassenschnitt fehlt",
    lang: "Zu diesem Abschluss gibt es keinen Terminal-Report. Entweder wurde er noch nicht gezogen, oder er liegt außerhalb des Exportzeitraums.",
  },
  MISSING_CLOSING: {
    kurz: "Abschluss fehlt",
    lang: "Zu diesem Terminal-Report gibt es keinen Kassenabschluss aus ecos.",
  },
  NEGATIVE_TIP: {
    kurz: "Trinkgeld negativ",
    lang: "Der Kassenschnitt liegt unter dem Z-Bon. Das ist fachlich unmöglich und deutet auf einen Datenfehler hin.",
  },
  SPLIT_REPORT: {
    kurz: "Report aufteilen",
    lang: "Ein Terminal-Report deckt mehrere Kassenabschlüsse ab. Der Vorschlag teilt ihn anteilig auf; die echten Werte stehen im VR-Portal.",
  },
  SPLIT_CLOSING: {
    kurz: "Abschluss aufteilen",
    lang: "Ein Kassenabschluss deckt mehrere Terminal-Reports ab. Der Vorschlag teilt ihn anteilig auf; die echten Werte stehen im VR-Portal.",
  },
  UNKNOWN_WORKSTATION: {
    kurz: "Kasse unbekannt",
    lang: "Die Terminal-ID oder der Kassenname ist in den Stammdaten nicht hinterlegt.",
  },
  LATE_CLOSING: {
    kurz: "Abschluss nachgeholt",
    lang: "Der Abschluss erfolgte tagsüber und gehört vermutlich zum Vorabend. Der Betriebstag wurde entsprechend vorgeschlagen.",
  },
  LATE_REPORT: {
    kurz: "Report verspätet",
    lang: "Zwischen Abschluss und Terminal-Report liegen mehrere Stunden. Die Zuordnung stimmt meist trotzdem.",
  },
  IMPLAUSIBLE_TIP: {
    kurz: "Trinkgeld unplausibel",
    lang: "Das Trinkgeld liegt über der hinterlegten Schwelle. Bitte prüfen, ob die Zuordnung stimmt.",
  },
  HIGH_TIP: {
    kurz: "Hohes Trinkgeld",
    lang: "Das Trinkgeld dieser Kasse an diesem Abend liegt über der Schwelle aus den Einstellungen. Oft steckt ein vergessener Kassenschnitt dahinter. Im Händler-Report prüfen und bestätigen.",
  },
  NEGATIVE_REVENUE: {
    kurz: "Umsatz negativ",
    lang: "Kasse oder Terminal melden einen negativen Umsatz, etwa wenn nur Pfand zurückgezahlt wurde. Prüfen und bestätigen.",
  },
  CARRYOVER_FROM_COUNTERPART: {
    // Nicht „vom Vortag“: der Rest steckt im nächsten Einsatz der Kasse, und
    // der kann Tage später sein.
    kurz: "Übertrag",
    lang: "An einem früheren Abend dieser Kasse wurde ein fehlender Wert von Hand erfasst. Fehlte dort der Kassenschnitt, steckt derselbe Betrag im nächsten Report dieser Kasse noch einmal, auch wenn der erst Tage später kommt.",
  },
  COUNTERPART_IN_SIBLING: {
    kurz: "Gegenwert nebenan",
    lang: "Der fehlende Wert steckt in einer anderen Session derselben Kasse am selben Tag. Das ist der Normalfall bei kumulativen Reports.",
  },
};

/** Quellformate. Die Codes stehen in der API, im UI hat niemand etwas davon. */
const SOURCE_LABELS: Record<string, string> = {
  POS_CLOSING: "ecos Kassenabschlüsse",
  TERMINAL_REPORT: "VR Terminal-Reports",
  ROSTER_HOURS: "Gastromatic Stunden",
  LEGACY: "Altbestand Excel",
};

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

const IMPORT_STATUS_LABELS: Record<string, string> = {
  PENDING: "wartet",
  PARSED: "gelesen",
  REVIEW: "in Prüfung",
  COMMITTED: "übernommen",
  FAILED: "fehlgeschlagen",
  REVERTED: "zurückgenommen",
};

export function importStatusLabel(status: string): string {
  return IMPORT_STATUS_LABELS[status] ?? status;
}

export function flagLabel(flag: string): string {
  return FLAG_LABELS[flag]?.kurz ?? flag;
}

export function flagDescription(flag: string): string {
  return FLAG_LABELS[flag]?.lang ?? flag;
}

export function isBlockingFlag(flag: string): boolean {
  return BLOCKING.has(flag);
}

/**
 * Ein Hinweis an einer Kassensession — in einem von drei Zuständen.
 *
 * Vorher unterschied nur die Farbe zwischen offen und geklärt, und der Tooltip
 * behauptete bei einer geklärten Session „nur ein Hinweis, blockiert nicht".
 * Das stimmte nicht: der Hinweis blockierte sehr wohl, bis jemand entschieden
 * hat. Derselbe Text in zwei Farben ohne Erklärung liest sich wie ein
 * Widerspruch — deshalb steht der Zustand jetzt im Chip selbst.
 */
export type FlagState = "zuKlaeren" | "hinweis" | "geklaert";

/**
 * In welchem der drei Zustände steht dieser Hinweis?
 *
 * Nur ein Hinweis, der die Freigabe blockiert, kann „geklärt" werden — die
 * übrigen mussten nie entschieden werden und bleiben, was sie sind.
 */
export function flagState(flag: string, confirmed?: boolean): FlagState {
  if (!BLOCKING.has(flag)) return "hinweis";
  return confirmed ? "geklaert" : "zuKlaeren";
}

const FLAG_STATE_STYLE: Record<FlagState, string> = {
  zuKlaeren: "chip-alert",
  hinweis: "chip-warn",
  geklaert: "chip-ok",
};

const FLAG_STATE_SUFFIX: Record<FlagState, string> = {
  zuKlaeren: " Blockiert die Freigabe, bis das geklärt ist.",
  hinweis: " Nur ein Hinweis, blockiert nicht.",
  geklaert: " War zu klären und ist entschieden. Die Entscheidung steht im Audit-Log.",
};

export function FlagChip({ flag, confirmed }: { flag: string; confirmed?: boolean }) {
  const state = flagState(flag, confirmed);
  return (
    <span className={FLAG_STATE_STYLE[state]} title={flagDescription(flag) + FLAG_STATE_SUFFIX[state]}>
      {state === "geklaert" ? `✓ ${flagLabel(flag)}` : flagLabel(flag)}
    </span>
  );
}

/** Legende zu den drei Zuständen. Einmal erklärt statt dreimal geraten. */
export function FlagLegend() {
  return (
    <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
      <span className="flex items-center gap-1.5">
        <span className="chip-alert">zu klären</span> braucht eine Entscheidung
      </span>
      <span className="flex items-center gap-1.5">
        <span className="chip-warn">Hinweis</span> blockiert nicht
      </span>
      <span className="flex items-center gap-1.5">
        <span className="chip-ok">✓ geklärt</span> entschieden, erledigt
      </span>
    </p>
  );
}

/**
 * Kleines Fragezeichen mit Erklärung.
 *
 * Früher als `title`: das zeigte der Browser nur nach einer Weile Stillhalten
 * mit der Maus, beim Anklicken, per Tastatur und am Touchscreen nie. Jetzt
 * öffnet die Blase bei Hover, Fokus und Klick und schließt mit Escape oder
 * einem Klick daneben. Sie hängt am `body` mit fester Position, damit keine
 * Tabelle mit `overflow` sie abschneidet und kein Versalien-Kopf auf sie abfärbt.
 */
export function Info({ text }: { text: string }) {
  const id = useId();
  const ref = useRef<HTMLButtonElement>(null);
  // Per Klick geöffnet bleibt offen, bis man woanders hinklickt; per Hover
  // geöffnet schließt beim Verlassen.
  const [open, setOpen] = useState<"hover" | "pinned" | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean } | null>(null);

  const place = useCallback(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(320, window.innerWidth - 32);
    const left = Math.max(16, Math.min(r.left + r.width / 2 - width / 2, window.innerWidth - width - 16));
    // Unten zu wenig Platz: darüber öffnen.
    const above = r.bottom + 160 > window.innerHeight && r.top > 160;
    setPos({ top: above ? r.top - 8 : r.bottom + 8, left, above });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== "Escape") return;
      if (e instanceof MouseEvent && ref.current?.contains(e.target as Node)) return;
      setOpen(null);
    };
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("keydown", close);
    document.addEventListener("mousedown", close);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("keydown", close);
      document.removeEventListener("mousedown", close);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={ref}
        type="button"
        className="ml-1 cursor-help select-none rounded-sm border border-line px-1 align-middle font-mono text-[10px] leading-4 text-muted hover:border-accent hover:text-accent"
        aria-label="Erklärung"
        aria-expanded={open !== null}
        aria-describedby={open ? id : undefined}
        onClick={(e) => {
          // Ein ? steht oft in einem klickbaren Kopf oder Link.
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => (o === "pinned" ? null : "pinned"));
        }}
        onMouseEnter={() => setOpen((o) => o ?? "hover")}
        onMouseLeave={() => setOpen((o) => (o === "hover" ? null : o))}
        onFocus={() => setOpen((o) => o ?? "hover")}
        onBlur={() => setOpen((o) => (o === "hover" ? null : o))}
      >
        ?
      </button>
      {open && pos
        ? createPortal(
            <div
              id={id}
              role="tooltip"
              className="info-bubble pointer-events-none fixed z-50 rounded border border-line bg-page px-3 py-2 text-left font-sans text-xs normal-case leading-relaxed tracking-normal text-ink"
              style={{
                top: pos.top,
                left: pos.left,
                width: Math.min(320, window.innerWidth - 32),
                transform: pos.above ? "translateY(-100%)" : undefined,
              }}
            >
              {text}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Überschrift mit Erklärung dahinter. */
export function LabelWithInfo({ label, info }: { label: string; info: string }) {
  return (
    <span className="inline-flex items-center">
      {label}
      <Info text={info} />
    </span>
  );
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: "Entwurf",
  CALCULATED: "berechnet",
  APPROVED: "freigegeben",
  LOCKED: "gesperrt",
  PROPOSED: "offen",
  CONFIRMED: "bestätigt",
  EXCLUDED: "ausgeschlossen",
  ACTIVE: "aktiv",
  INACTIVE: "inaktiv",
  OPEN: "offen",
  EXPORTED: "exportiert",
  PAID: "ausgezahlt",
};

/** Status in Worten, wie der Chip ihn zeigt. */
export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function StatusChip({ status }: { status: string }) {
  const map: Record<string, string> = {
    DRAFT: "chip-muted",
    CALCULATED: "chip-warn",
    APPROVED: "chip-ok",
    LOCKED: "chip-muted",
    PROPOSED: "chip-warn",
    CONFIRMED: "chip-ok",
    EXCLUDED: "chip-muted",
    ACTIVE: "chip-ok",
    INACTIVE: "chip-muted",
    OPEN: "chip-warn",
    EXPORTED: "chip-ok",
    PAID: "chip-ok",
  };
  const hints: Record<string, string> = {
    DRAFT: "Noch nicht gerechnet oder seit der letzten Rechnung geändert.",
    CALCULATED: "Gerechnet und ausgeglichen, aber noch nicht freigegeben.",
    APPROVED: "Freigegeben. Zählt in eine Auszahlungsperiode.",
    LOCKED: "Gesperrt, weil ausgezahlt oder Altbestand. Wird nie neu gerechnet.",
    PROPOSED: "Vorschlag aus dem Abgleich, noch nicht bestätigt.",
    CONFIRMED: "Von einer Person geprüft und bestätigt.",
    EXCLUDED: "Ausgeschlossene Kasse, fließt nie in einen Topf.",
    ACTIVE: "Wird verwendet.",
    INACTIVE: "Deaktiviert. Historische Daten bleiben erhalten.",
    OPEN: "Offen, Beträge können sich noch ändern.",
    EXPORTED: "Exportiert. Die Überweisung ist angestoßen.",
    PAID: "Ausgezahlt.",
  };
  return (
    <span className={map[status] ?? "chip-muted"} title={hints[status] ?? ""}>
      {statusLabel(status)}
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="rounded border border-dashed border-line px-6 py-10 text-center text-sm text-muted">
      {children}
    </div>
  );
}

export function Spinner({ label = "Lädt" }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 px-6 py-10 text-sm text-muted">
      <span className="h-3 w-3 animate-spin rounded-full border border-line border-t-accent" />
      {label}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "Unbekannter Fehler";
  return (
    <div className="rounded border border-alert/30 bg-alert/5 px-4 py-3 text-sm text-alert">
      {message}
    </div>
  );
}

/** Kennzahl im Kopfbereich. */
export function Stat({
  label,
  value,
  tone = "default",
  info,
}: {
  label: string;
  value: string;
  tone?: "default" | "alert" | "ok";
  info?: string;
}) {
  const color = tone === "alert" ? "text-alert" : tone === "ok" ? "text-ok" : "text-ink";
  return (
    <div className="border-l border-line pl-4">
      <div className="label mb-1">
        {label}
        {info ? <Info text={info} /> : null}
      </div>
      <div className={`font-mono text-xl tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
